/**
 * PK-QA-6 — shared "Add Information" draft (context text + pending files).
 *
 * Lifts `AddInformationPanel`'s previously-local `text` / `pendingFiles` state UP
 * so BOTH the panel (its own "Save Information" button) AND its sibling
 * `PreKickoffPanel` (the "Save & Run Analysis" combined action) share ONE dirty
 * flag and ONE multipart save. Before PK-QA-6 the text/files lived only inside the
 * panel, so `runAnalysis()` posted with an empty body and any unsaved context/files
 * never reached the analysis (decision-log 2026-07-20 PK-QA-6).
 *
 * Dirtiness is decided by the SHARED project dirty-model
 * (`projectUnsavedModel.hasUnsavedChanges`) so the rule matches the header's
 * navigate-away guard; the save reuses `useSaveProjectChanges` (PATCH /projects/:id,
 * `manual_context` + `files[]`) — zero backend/contract change, pure orchestration
 * of two existing endpoints.
 *
 * Seeding mirrors the legacy `AddInformationPanel` (`key={projectId}` remount):
 * the text is seeded ONCE from the persisted value and is NOT re-synced on every
 * parent re-render (a link/file mutation refetch must never clobber an in-progress
 * edit); a project SWITCH re-seeds. A local `savedBaseline` advances on a
 * successful save so the card returns to "clean" immediately, without waiting for
 * the detail-card refetch to re-seed `savedManualText` (same idiom as
 * `useProjectUnsaved`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { hasUnsavedChanges, resetUnsavedUpdates } from '../project/projectUnsavedModel.js';
import { useSaveProjectChanges, type SaveFile } from '../project/projectSaveApi.js';
import { useRegisterUnsavedSource } from '../ui/UnsavedGuard.js';

/** The shared draft API consumed by both `AddInformationPanel` and `PreKickoffPanel`. */
export interface AddInformationDraftApi {
  /** The current context-textarea text. */
  readonly text: string;
  /** Update the context text. */
  readonly setText: (text: string) => void;
  /** Files picked but not yet uploaded. */
  readonly pendingFiles: readonly File[];
  /** Append newly-picked files to the pending queue. */
  readonly addFiles: (files: readonly File[]) => void;
  /** Remove one pending (not-yet-uploaded) file by index. */
  readonly removePendingFile: (index: number) => void;
  /** True when there is unsaved context text or at least one pending file. */
  readonly dirty: boolean;
  /** Whether the save is currently in flight. */
  readonly saving: boolean;
  /**
   * Persist the context text + pending files (multipart PATCH /projects/:id).
   * Resolves on success (after clearing the pending-files queue) and REJECTS on
   * failure — callers own the success/error toast.
   */
  readonly save: () => Promise<void>;
  /**
   * Drop the unsaved context edit + the pending-files queue (back to the last
   * persisted state). Used by the navigation guard's "Discard and exit".
   */
  readonly discard: () => void;
}

export interface UseAddInformationDraftArgs {
  readonly projectId: number;
  /** The persisted `manual_context.text` — the dirty-compare baseline + seed. */
  readonly savedManualText: string;
}

export function useAddInformationDraft(args: UseAddInformationDraftArgs): AddInformationDraftApi {
  const { projectId, savedManualText } = args;
  const saveMutation = useSaveProjectChanges();

  // Seed once from the persisted value; not re-synced on unrelated re-renders.
  const [text, setText] = useState(savedManualText);
  const [pendingFiles, setPendingFiles] = useState<readonly File[]>([]);

  // The baseline the text is diffed against. Seeds from the project's saved text,
  // follows it when it changes (e.g. a project switch), and ADVANCES on a
  // successful save so the card returns to "clean" without waiting for the refetch.
  const [savedBaseline, setSavedBaseline] = useState(savedManualText);
  useEffect(() => setSavedBaseline(savedManualText), [savedManualText]);

  // A project switch re-seeds the editable text + clears the pending queue
  // (parity with the legacy `key={projectId}` remount). Guarded by a ref so an
  // unrelated re-render (same project) never clobbers an in-progress edit.
  const lastProjectRef = useRef(projectId);
  useEffect(() => {
    if (lastProjectRef.current !== projectId) {
      lastProjectRef.current = projectId;
      setText(savedManualText);
      setPendingFiles([]);
    }
  }, [projectId, savedManualText]);

  const dirty = hasUnsavedChanges({
    // Only the manual-context text + pending files matter here; the inline-edit
    // bag (client/role/contact) belongs to the header, so it stays empty.
    unsavedUpdates: resetUnsavedUpdates(),
    manualText: text,
    savedManualText: savedBaseline,
    pendingFiles: pendingFiles.map((f) => ({ name: f.name, file: f })),
    hasCurrentProject: true,
  });

  const addFiles = useCallback((files: readonly File[]): void => {
    if (files.length === 0) return;
    setPendingFiles((prev) => [...prev, ...files]);
  }, []);

  const removePendingFile = useCallback((index: number): void => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const save = useCallback(async (): Promise<void> => {
    const files: SaveFile[] = pendingFiles.map((f) => ({ name: f.name, file: f }));
    await saveMutation.mutateAsync({
      projectId,
      updates: { manual_context: { text } },
      files,
    });
    // DB is now the source of truth — drop the staged files and advance the
    // baseline so `dirty` clears immediately.
    setPendingFiles([]);
    setSavedBaseline(text);
  }, [pendingFiles, projectId, text, saveMutation]);

  const discard = useCallback((): void => {
    setText(savedBaseline);
    setPendingFiles([]);
  }, [savedBaseline]);

  // E02-F03-T08 — publish the draft to the navigation guard, so leaving the
  // project card with an unsaved context edit / staged file opens the
  // Save/Discard/Cancel dialog (and raises the browser's beforeunload confirm on
  // a tab close) instead of dropping the work silently. The provider keeps ALL
  // registered sources, so this coexists with the header's own registration:
  // whichever is dirty blocks, and "Save and exit" saves each dirty one.
  useRegisterUnsavedSource({
    getDraft: () => ({ unsavedUpdates: dirty ? { _dirty: true } : {} }),
    onSave: () => save(),
    onDiscard: () => discard(),
  });

  return {
    text,
    setText,
    pendingFiles,
    addFiles,
    removePendingFile,
    dirty,
    saving: saveMutation.isPending,
    save,
    discard,
  };
}
